// src/stations/sprint/questionPool.ts — Question Pool Rotation & Set Progression Manager (FR-QUEST-005, FR-QUEST-006)
// Partitions question banks into discrete sets, prioritises unseen questions, and handles mistake review.

import type { QuestPaperDomain } from '@storage/sprintStorage';
import { SprintStorage } from '@storage/sprintStorage';
import type { QuestQuestion } from './questEngine';

export interface PoolSelectionOptions {
  paper: QuestPaperDomain;
  setCount?: number;
  mode?: 'standard' | 'mistakes_only';
  customQuestionIds?: string[];
}

export interface PoolSelectionResult {
  questions: QuestQuestion[];
  setIndex: number;
  totalSetsInBank: number;
  isExhaustedCycle: boolean;
  unseenRemaining: number;
  mistakeCountAvailable: number;
  modeUsed: 'standard' | 'mistakes_only';
}

export class QuestionPool {
  /**
   * Select a set of questions calibrated for a sprint round.
   * Prioritises unseen questions before recycling, and provides targeted mistake review.
   */
  static selectQuestionSet(
    allQuestions: QuestQuestion[],
    options: PoolSelectionOptions,
    storage: typeof SprintStorage = SprintStorage
  ): PoolSelectionResult {
    const paper = options.paper;
    const targetCount = options.setCount ?? 5;
    const requestedMode = options.mode ?? 'standard';

    // Filter questions belonging to the active paper
    const paperQuestions = allQuestions.filter((q) => q.paper === paper);
    const totalSetsInBank = Math.max(1, Math.ceil(paperQuestions.length / targetCount));
    const completedSets = storage.getCompletedSetCount(paper);
    const setIndex = (completedSets % totalSetsInBank) + 1;

    const data = storage.load();
    const registry = data.questionRegistry;

    // Identify mistakes available in this paper
    const mistakeQuestions = paperQuestions.filter((q) => {
      const stats = registry[q.id];
      if (!stats) return false;
      return !stats.lastAttemptCorrect || stats.timesCorrect < stats.timesSeen;
    });

    // Handle 'mistakes_only' mode
    if (requestedMode === 'mistakes_only' && mistakeQuestions.length > 0) {
      // Sort mistakes by lowest accuracy and most recent mistake
      const sortedMistakes = [...mistakeQuestions].sort((a, b) => {
        const statsA = registry[a.id];
        const statsB = registry[b.id];
        const accA = statsA && statsA.timesSeen > 0 ? statsA.timesCorrect / statsA.timesSeen : 0;
        const accB = statsB && statsB.timesSeen > 0 ? statsB.timesCorrect / statsB.timesSeen : 0;
        return accA - accB;
      });

      const selected = sortedMistakes.slice(0, targetCount);
      return {
        questions: selected,
        setIndex,
        totalSetsInBank,
        isExhaustedCycle: false,
        unseenRemaining: paperQuestions.filter((q) => !registry[q.id] || registry[q.id]!.timesSeen === 0).length,
        mistakeCountAvailable: mistakeQuestions.length,
        modeUsed: 'mistakes_only',
      };
    }

    // Standard mode: Partition into unseen vs seen
    const unseen: QuestQuestion[] = [];
    const seen: QuestQuestion[] = [];

    for (const q of paperQuestions) {
      const stats = registry[q.id];
      if (!stats || stats.timesSeen === 0) {
        unseen.push(q);
      } else {
        seen.push(q);
      }
    }

    const isExhausted = unseen.length === 0;

    let selected: QuestQuestion[] = [];

    const startIndex = (setIndex - 1) * targetCount;
    if (!isExhausted && startIndex + targetCount <= paperQuestions.length) {
      // Clean partition for discrete set index
      selected = paperQuestions.slice(startIndex, startIndex + targetCount);
    } else if (unseen.length >= targetCount) {
      // Clean unseen set available
      selected = unseen.slice(0, targetCount);
    } else {
      // Take all remaining unseen questions
      selected = [...unseen];

      // Sort seen questions: lowest accuracy first, then oldest lastAttemptTimestamp
      const sortedSeen = [...seen].sort((a, b) => {
        const statsA = registry[a.id];
        const statsB = registry[b.id];
        const accA = statsA && statsA.timesSeen > 0 ? statsA.timesCorrect / statsA.timesSeen : 0;
        const accB = statsB && statsB.timesSeen > 0 ? statsB.timesCorrect / statsB.timesSeen : 0;
        if (accA !== accB) return accA - accB;
        const timeA = statsA ? statsA.lastAttemptTimestamp : 0;
        const timeB = statsB ? statsB.lastAttemptTimestamp : 0;
        return timeA - timeB;
      });

      // Fill remaining slots from sorted seen questions without duplicating
      const selectedIds = new Set(selected.map((q) => q.id));
      for (const q of sortedSeen) {
        if (selected.length >= targetCount) break;
        if (!selectedIds.has(q.id)) {
          selected.push(q);
          selectedIds.add(q.id);
        }
      }
    }

    // Safety fallback if question bank is smaller than targetCount
    if (selected.length === 0) {
      selected = paperQuestions.slice(0, targetCount);
    }

    const unseenRemainingAfterSelection = Math.max(0, unseen.length - selected.filter((q) => !registry[q.id] || registry[q.id]!.timesSeen === 0).length);

    return {
      questions: selected,
      setIndex,
      totalSetsInBank,
      isExhaustedCycle: isExhausted,
      unseenRemaining: unseenRemainingAfterSelection,
      mistakeCountAvailable: mistakeQuestions.length,
      modeUsed: 'standard',
    };
  }
}
